import cx from 'classnames';
import { Field, Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useHistory } from 'react-router';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { createInverterAuthentication } from '../../../api/Optimise';
import { OnboardingContext } from '../../../context/OnboardingContext';
import ErrorLabel from '../../../ui/ErrorLabel';
import LoopSpinner from '../../../ui/LoopSpinner';
import InfoModal from '../../../ui/Modal/InfoModal';
import { min } from '../../../utils/breakpoints';
import generateRandomString from '../../../utils/generateRandomString';

const borderStyles = `
    border: 1px solid ${(props) => props.theme.colors.light};
    border-radius: 4px;
    padding: 20px 20px 0 20px;
  `;

const StyledFormContainer = styled.div`
  margin-top: 2rem;
  text-align: left;
  ${({ logo }) => logo && borderStyles};

  /* Mask text input to look like a password field without using type="password",
     which triggers password manager autofill heuristics */
  .masked-input {
    -webkit-text-security: disc;
  }
`;

const StyledLogo = styled.img`
  margin: 0 auto;
  display: block;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  outline: inherit;
  text-decoration: underline !important;
  color: ${(props) => props.theme.colors.primary};
  display: inline-block;
  font-size: 14px;

  ${min.sm} {
    font-size: 16px;
  }
`;

const BaseConnectionContainer = ({ fields, logo = null, exampleModalContent = null, inverterManufacturer }) => {
  const theme = useTheme();
  const history = useHistory();
  const [error, setError] = useState(undefined);
  const { setProgressPercentage, setManufacturerDetails } = useContext(OnboardingContext);
  const [showInfo, setShowInfo] = useState(false);
  const [infoContent, setInfoContent] = useState(undefined);

  const validationSchema = Yup.object().shape(
    fields.reduce((acc, field) => {
      acc[field.name] = field.validation || Yup.string().required(`${field.label} is required`);
      return acc;
    }, {})
  );

  const showInfoModal = (e, info) => {
    e.stopPropagation();
    setInfoContent(info);
    setShowInfo(true);
  };

  const prepareValues = (values) => {
    if (!values?.api_password) {
      values.api_password = generateRandomString(20);
    }
    return values;
  };

  const onSubmit = async (values) => {
    setError(undefined);
    setManufacturerDetails(values);
    try {
      await createInverterAuthentication({
        ...prepareValues(values),
        api_name: inverterManufacturer.toUpperCase(),
      });
      history.push('/onboarding/connected');
    } catch (error) {
      setError(error);
      console.error(error);
    }
  };

  useEffect(() => {
    setProgressPercentage(90);
  }, []);

  return (
    <>
      <h1 className="mb-3">Connect to your {inverterManufacturer} system</h1>
      <div>
        <p>Finally, please provide the details that {theme.language.appName} needs to connect to your system.</p>
        <StyledButton
          onClick={(e) =>
            showInfoModal(
              e,
              <>
                These details enable {theme.language.appName} to talk to your battery and ensure it operates optimally
                based on our forecasts for your home's generation and demand.
              </>
            )
          }
        >
          Why does {theme.language.appName} need to know this?
        </StyledButton>
        <StyledFormContainer logo={logo}>
          {logo && <StyledLogo src={logo} alt="Logo" />}
          <Formik
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            initialValues={fields.reduce((acc, field) => {
              acc[field.name] = '';
              return acc;
            }, {})}
            validateOnChange
          >
            {({ handleSubmit, isValid, isSubmitting }) => (
              <Form onSubmit={handleSubmit} autoComplete="off">
                {fields.map((field) => (
                  <Field key={field.name} name={field.name}>
                    {({ field: formikField, meta }) => (
                      <Form.Group controlId={field.name}>
                        <Form.Label>{field.label}</Form.Label>
                        <Form.Control
                          type="text"
                          {...formikField}
                          isValid={!meta.error && meta.touched}
                          placeholder={field.placeholder || field.label}
                          className={cx({ error: !!meta.error }, { 'masked-input': field.type === 'password' })}
                          autoComplete="off"
                          data-1p-ignore
                          data-lpignore
                          data-form-type="other"
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </Form.Group>
                    )}
                  </Field>
                ))}

                {exampleModalContent && (
                  <p className={cx({ 'text-center': logo })}>
                    If you need help finding these details, please take a look at{' '}
                    <StyledButton type="button" onClick={(e) => showInfoModal(e, exampleModalContent)}>
                      this example
                    </StyledButton>
                    .
                  </p>
                )}

                {error && (
                  <ErrorLabel label={`Something went wrong - please check your details and try again. (${error})`} />
                )}

                <Form.Group>
                  {!isSubmitting ? (
                    <Button variant="primary" disabled={!isValid || isSubmitting} block type="submit">
                      Continue
                    </Button>
                  ) : (
                    <LoopSpinner />
                  )}
                </Form.Group>
              </Form>
            )}
          </Formik>
        </StyledFormContainer>
      </div>

      <InfoModal show={showInfo} onClose={() => setShowInfo(false)} infoContent={infoContent} />
    </>
  );
};

export default BaseConnectionContainer;
